/**
 * rb-conventions.js — per-region hiring-convention profiles.
 *
 * Pure leaf module. The controller calls RbConventions.setProfiles() once at
 * init with the @json-injected config('resume-conventions') table (keyed by
 * locale). This module indexes it by format_id (the value stored in
 * model.meta.region) and answers convention questions: photo/field defaults,
 * recommended templates, document name, customary length.
 *
 * Convention data only — NEVER conversion statistics. Any number shown to the
 * user comes from a cited external source via the profile's sources_key.
 *
 * globalThis.RbConventions
 */
(function () {
    'use strict';

    var _byLocale = {};   /* locale -> profile */
    var _byFormat = {};    /* format_id -> profile */
    var FIELDS = ['dob', 'marital', 'nationality', 'religion', 'gender', 'signature'];

    function setProfiles(obj) {
        _byLocale = obj && typeof obj === 'object' ? obj : {};
        _byFormat = {};
        Object.keys(_byLocale).forEach(function (loc) {
            var p = _byLocale[loc];
            if (p && p.format_id) {
                p._locale = loc;
                _byFormat[p.format_id] = p;
            }
        });
    }

    /* The format_id a given UI locale defaults to. */
    function defaultRegion(locale) {
        var p = _byLocale[(locale || 'en').toString().slice(0, 2)];
        return (p && p.format_id) || 'us';
    }

    /* Profile for a format_id; falls back to the US profile, then to a safe
       minimal profile so callers never get undefined. */
    function get(region) {
        return _byFormat[region] || _byFormat.us || {
            format_id: 'us', doc_name: 'Resume',
            length: { min: 1, max: 2, unit: 'page' }, order: 'reverse-chronological',
            photo: 'off', date_format: 'MM/YYYY',
            fields: { dob: 'off', marital: 'off', nationality: 'off', religion: 'off', gender: 'off', signature: 'off' },
            recommended_templates: ['classic'], convention_key: 'conv_us', sources_key: 'conv_us_src'
        };
    }

    /* Every format_id, in config order — for the region <select>. */
    function regions() {
        return Object.keys(_byLocale).map(function (loc) {
            var p = _byLocale[loc];
            return { id: p.format_id, doc_name: p.doc_name };
        });
    }

    /* 'on' | 'optional' | 'off' for a personal field in a region. */
    function fieldState(region, field) {
        var p = get(region);
        if (field === 'photo') return p.photo || 'off';
        return (p.fields && p.fields[field]) || 'off';
    }

    /* Boolean cultural map (what the model.cultural flags should default to
       when a region is first selected): only 'on' fields start checked. */
    function culturalDefaults(region) {
        var out = { photo: fieldState(region, 'photo') === 'on' };
        FIELDS.forEach(function (f) { out[f] = fieldState(region, f) === 'on'; });
        return out;
    }

    function recommendedTemplates(region) {
        var p = get(region);
        return Array.isArray(p.recommended_templates) ? p.recommended_templates.slice() : [];
    }

    function isRecommended(templateId, region) {
        return recommendedTemplates(region).indexOf(templateId) > -1;
    }

    function dateFormat(region) {
        return get(region).date_format || 'MM/YYYY';
    }

    function pageSize(region) {
        return get(region).page_size === 'letter' ? 'letter' : 'a4';
    }

    function docName(region) {
        return get(region).doc_name || 'Resume';
    }

    globalThis.RbConventions = {
        FIELDS: FIELDS,
        setProfiles: setProfiles,
        defaultRegion: defaultRegion,
        get: get,
        regions: regions,
        fieldState: fieldState,
        culturalDefaults: culturalDefaults,
        recommendedTemplates: recommendedTemplates,
        isRecommended: isRecommended,
        dateFormat: dateFormat,
        pageSize: pageSize,
        docName: docName
    };
})();
